import { api } from "./client";

export type ShareRow = {
  id: string;
  label: string;
  share_name: string;
  path: string;
  permissions: { principal: string; access: string }[];
  status: "pending" | "active" | "failed" | "removing" | "removed";
  host_agent: string;
  host_agent_hostname: string;
  host_agent_label: string;
  created_at: string;
  updated_at: string;
};

export type CreateSharePayload = {
  host_agent_id: string;
  path: string;
  // label is no longer user-input — the backend derives it from the folder
  // basename and the agent prefixes "kraetdtech – " when naming the drive.
  // Kept optional in the type for forward-compat with older clients only.
  label?: string;
  share_name?: string;
  access: "read" | "write";
};

export async function listShares() {
  const { data } = await api.get<{ results: ShareRow[] }>("/shares/");
  return data;
}

export async function createShare(payload: CreateSharePayload) {
  const { data } = await api.post<ShareRow>("/shares/", payload);
  return data;
}

export async function deleteShare(id: string) {
  await api.delete(`/shares/${id}`);
}

// Test access — fire a one-shot reachability probe at a shared folder.
// Returns a task ID the caller polls until terminal. The task's result
// payload tells you what actually happened on a client PC (ok / denied /
// unreachable / not_found / error) — richer than a "looks active in the
// dashboard" assumption.
export type TestAccessTicket = {
  task_id: string;
  status: string;
  share_id: string;
  prober_agent_id: string;
};

export type TestAccessStatus = {
  task_id: string;
  status: "pending" | "running" | "succeeded" | "failed" | "expired" | "cancelled";
  result: {
    outcome?: "ok" | "denied" | "unreachable" | "not_found" | "error";
    detail?: string;
  };
  error_message: string;
  created_at: string;
  completed_at: string | null;
};

export async function testShareAccess(id: string) {
  const { data } = await api.post<TestAccessTicket>(`/shares/${id}/test-access`);
  return data;
}

export async function getTestAccessStatus(taskId: string) {
  const { data } = await api.get<TestAccessStatus>(`/shares/test-accesses/${taskId}`);
  return data;
}
